"use client";
import React, { useState, useEffect, useRef, useCallback } from "react";
import Link from "next/link";

import dynamic from "next/dynamic";
import { decode } from "html-entities";
const ZoomInWrapper = dynamic(() => import("../motion/ZoomInWrapper"));
const FaArrowRight = dynamic(() =>
  import("react-icons/fa").then((mod) => mod.FaArrowRight)
);

const cardGradients = [
  "bg-gradient-to-br from-orange-500 via-yellow-400 to-yellow-200", // Warm Sunset
  "bg-gradient-to-br from-blue-400 via-cyan-300 to-cyan-200", // Ocean Breeze
  "bg-gradient-to-br from-indigo-700 via-purple-500 to-indigo-400", // Royal Purple
  "bg-gradient-to-br from-pink-500 via-fuchsia-400 to-purple-300", // Coral Dreams
  "bg-gradient-to-br from-green-600 via-emerald-500 to-teal-400", // Lush Forest
  "bg-gradient-to-br from-orange-400 via-amber-300 to-yellow-200", // Golden Hour
  "bg-gradient-to-br from-pink-500 via-rose-400 to-rose-300", // Berry Smoothie
  "bg-gradient-to-br from-purple-600 via-violet-500 to-pink-500", // Electric Violet
  "bg-gradient-to-br from-teal-400 via-cyan-300 to-sky-200", // Aqua Splash
  "bg-gradient-to-br from-red-500 via-rose-400 to-pink-300", // Rose Quartz
];

function BlogCard({ keyId, index, post, imageUrl }) {
  return (
    <ZoomInWrapper delay={index * 0.01} key={keyId}>
      <Link href={`/${post?.slug}`} className="group" key={keyId}>
        <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
          <div
            className={`relative bg-gradient-to-br ${
              cardGradients[index % cardGradients.length]
            } w-full h-48`}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex flex-col justify-end p-6">
              <p className="text-white text-lg font-medium mb-2">
                {decode(post?.title?.rendered || "")}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-neutral-200 text-sm">Read More</span>
                <FaArrowRight className="text-white group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </div>
      </Link>
    </ZoomInWrapper>
  );
}

export const AllBlogs = () => {
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const loader = useRef(null);

  const fetchPosts = useCallback(async () => {
    if (!hasMore) return;
    try {
      const res = await fetch(`https://wp.netpractice.app/wp-json/wp/v2/posts?per_page=20&page=${page}`);
      if (!res.ok) {
        if (res.status === 400) {
          setHasMore(false);
        }
        return;
      }
      const data = await res.json();
      setPosts(prev => [...prev, ...data]);
      if (data.length < 20) setHasMore(false);
    } catch (e) {
      console.error("Error loading posts:", e);
    }
  }, [page, hasMore]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 1.0 }
    );
    if (loader.current) observer.observe(loader.current);
    return () => loader.current && observer.unobserve(loader.current);
  }, [hasMore]);

  return (
    <>
      {posts.map((post, i) => (
        <div key={post.slug + i}>
          <BlogCard keyId={post.slug} index={i} post={post} />
        </div>
      ))}
      <div ref={loader} className="h-10" />
    </>
  );
};
